import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { AuthState, Clock, DsLoggingService, LogoutSurface } from '@cubigo/digital-signage';

import { OAuthInfoEvent, OAuthService, OAuthStorage, TokenResponse } from 'angular-oauth2-oidc';
import { Observable, defer, filter, from, fromEvent, merge, tap } from 'rxjs';

import { CredentialRevocationService } from './credential-revocation.service';

// A display re-authenticates only by someone walking up to it, so every constant here is biased
// towards holding on to a working session rather than towards ending one promptly.
const WATCHDOG_INTERVAL_MS = 60_000;
const REFRESH_AT_FRACTION = 0.6;
const FALLBACK_MARGIN_MS = 5 * 60_000;
const KEEPALIVE_MS = 24 * 60 * 60_000;
const REVOCATION_CONFIRMATIONS = 3;
const STORED_AT_KEY = 'access_token_stored_at';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly credentialRevocation = inject(CredentialRevocationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly oauthService = inject(OAuthService);
  private readonly storage = inject(OAuthStorage);
  private readonly router = inject(Router);
  private readonly logging = inject(DsLoggingService);
  private readonly clock = inject(Clock);
  private readonly authState = signal<AuthState>(AuthState.Anonymous);
  private readonly failures = signal(0);
  private readonly revocationSignals = signal(0);
  private readonly lastRefresh = signal<number | null>(null);
  private retry?: { controller: AbortController; dueAt: number };
  private refreshing?: Promise<boolean>;
  private initialized = false;
  private loggingOut = false;
  private destroyed = false;

  public readonly state = this.authState.asReadonly();
  public readonly refreshFailures = this.failures.asReadonly();
  public readonly lastRefreshAt = this.lastRefresh.asReadonly();

  public get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public get isTokenValid(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Only a refresh token can recover a session; a stale access token is not something to refresh with.
  public get canRefresh(): boolean {
    return Boolean(this.oauthService.getRefreshToken());
  }

  public get accessTokenExpiresInMs(): number | null {
    const expiration = this.oauthService.getAccessTokenExpiration();

    return expiration ? expiration - this.clock.now().toEpochMilli() : null;
  }

  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    const watchdog = this.clock.every(WATCHDOG_INTERVAL_MS, 0, () => this.watchdogTick());
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      watchdog.dispose();
      this.cancelRetry();
    });
    this.transition(this.isTokenValid ? AuthState.Valid : this.canRefresh ? AuthState.Recovering : AuthState.Anonymous);
    this.oauthService.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof OAuthInfoEvent && event.type === 'token_expires' && event.info === 'access_token') {
        void this.refreshToken();
      }
    });
    // A suspended device resumes with timers that never fired, so every sign of life re-checks the token.
    merge(
      fromEvent(window, 'online'),
      fromEvent(window, 'focus'),
      fromEvent(window, 'pageshow'),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onWake());
  }

  public async validateAndRefreshToken(): Promise<boolean> {
    this.initialize();

    if (this.isTokenValid) {
      return true;
    }

    if (!this.canRefresh) {
      return false;
    }

    await this.refreshToken();

    // A failed refresh still admits the display while a retry is pending; revocation has already cleared the token.
    return this.isTokenValid || this.canRefresh;
  }

  public login(email: string, pinCode: string): Observable<TokenResponse> {
    return defer(() => from(this.oauthService.fetchTokenUsingPasswordFlow(email, pinCode))).pipe(
      tap(() => {
        this.cancelRetry();
        this.failures.set(0);
        this.revocationSignals.set(0);
        this.lastRefresh.set(this.clock.now().toEpochMilli());
        this.transition(AuthState.Valid);
        this.logging.trackEvent('login_succeeded');
      })
    );
  }

  public refreshToken(): Promise<boolean> {
    if (this.refreshing) {
      return this.refreshing;
    }

    // A frozen device can leave the backoff timer un-fired; past its due time it is dead, not pending,
    // and treating it as pending would wedge the display out of every later refresh attempt.
    if (this.retry && !this.retry.controller.signal.aborted
      && this.clock.now().toEpochMilli() < this.retry.dueAt) {
      return Promise.resolve(false);
    }

    if (this.destroyed || this.loggingOut || !this.canRefresh) {
      return Promise.resolve(false);
    }

    this.cancelRetry();
    this.transition(AuthState.Refreshing);
    this.refreshing = this.performRefresh().finally(() => {
      this.refreshing = undefined;
    });

    return this.refreshing;
  }

  public async logoutAndNavigate(surface: LogoutSurface): Promise<void> {
    if (this.loggingOut) {
      return;
    }

    this.loggingOut = true;
    this.cancelRetry();
    // OAuth writes refreshed credentials before resolving; let an in-flight refresh settle before clearing them.
    await this.refreshing;
    this.logging.trackEvent('logout_performed', {
      reason: 'user-initiated', surface, tokenValid: this.isTokenValid, refreshFailures: this.failures()
    });
    this.oauthService.logOut();
    this.transition(AuthState.Anonymous);
    this.loggingOut = false;
    await this.router.navigate(['/login']);
  }

  // Wall-clock polling, rather than one long timer, is what survives a device that sleeps through its expiry.
  private watchdogTick(): void {
    if (this.destroyed || this.loggingOut || this.refreshing || !this.canRefresh || !this.shouldRefreshNow()) {
      return;
    }

    void this.refreshToken();
  }

  private onWake(): void {
    if (this.destroyed || this.loggingOut || !this.canRefresh || !this.shouldRefreshNow()) {
      return;
    }

    // The pending backoff was scheduled against a device that was asleep; waking up is newer information.
    this.cancelRetry();
    void this.refreshToken();
  }

  private shouldRefreshNow(): boolean {
    if (!this.isTokenValid) {
      return true;
    }

    const now = this.clock.now().toEpochMilli();
    const expiration = this.oauthService.getAccessTokenExpiration();
    const storedAt = this.storedAt();
    const lastTouch = this.lastRefresh() ?? storedAt;

    // The sliding refresh window on the server is the real session limit, so the token endpoint gets
    // touched daily regardless of how long the access token itself happens to live.
    if (lastTouch !== null && now - lastTouch >= KEEPALIVE_MS) {
      return true;
    }

    if (!expiration) {
      return false;
    }

    return now >= (storedAt === null || expiration <= storedAt
      ? expiration - FALLBACK_MARGIN_MS
      : storedAt + REFRESH_AT_FRACTION * (expiration - storedAt));
  }

  // The library keeps this timestamp protected, but it is the only record of when the token was issued
  // and it survives a restart, which an in-memory signal does not.
  private storedAt(): number | null {
    const stored = Number(this.storage.getItem(STORED_AT_KEY));

    return Number.isFinite(stored) && stored > 0 ? stored : null;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      await this.oauthService.refreshToken();

      if (this.destroyed || this.loggingOut) {
        return false;
      }

      if (!this.isTokenValid) {
        this.recover();

        return false;
      }

      this.failures.set(0);
      this.revocationSignals.set(0);
      this.lastRefresh.set(this.clock.now().toEpochMilli());
      this.transition(AuthState.Valid);

      return true;
    } catch (error: unknown) {
      if (this.destroyed || this.loggingOut) {
        return false;
      }

      this.handleRefreshFailure(error);

      return false;
    }
  }

  // Clearing credentials costs an on-site visit, so it takes a server that rejects them repeatedly
  // while this display is demonstrably online, not one bad response during a deploy.
  private handleRefreshFailure(error: unknown): void {
    if (!this.credentialRevocation.isRevocation(error)) {
      this.revocationSignals.set(0);
      this.recover();

      return;
    }

    // Offline, a rejection is indistinguishable from a captive portal answering for the token endpoint.
    if (!navigator.onLine) {
      this.recover();

      return;
    }

    this.revocationSignals.update(value => value + 1);

    if (this.revocationSignals() < REVOCATION_CONFIRMATIONS) {
      this.logging.trackEvent('credentials_revocation_suspected', {
        signals: this.revocationSignals(), required: REVOCATION_CONFIRMATIONS
      });
      this.recover();

      return;
    }

    this.revoke();
  }

  // Storage holds credentials only while they can still work, so nothing downstream has to recognise a dead token.
  private revoke(): void {
    this.failures.update(value => value + 1);
    this.cancelRetry();
    // Tracked before the credentials go, so the event still describes the session that was lost.
    this.logging.trackEvent('credentials_revoked', { reason: 'credentials-revoked' });
    this.oauthService.logOut(true);
    this.transition(AuthState.Revoked);
    // Nothing on the device can recover from here. Without this the display keeps its route, every feed
    // fails unauthenticated and it settles on "nothing to display" — a dead board that looks like empty content.
    void this.router.navigate(['/login']);
  }

  private recover(): void {
    this.failures.update(value => value + 1);
    this.transition(AuthState.Recovering);
    const cap = Math.min(30 * 60_000, 30_000 * 2 ** Math.min(this.failures() - 1, 6));
    const delayMs = Math.max(1000, Math.floor(Math.random() * cap));
    const controller = new AbortController();
    const retry = { controller, dueAt: this.clock.now().toEpochMilli() + delayMs };
    this.retry = retry;
    void this.clock.after(delayMs, controller.signal).then(() => {
      if (this.retry === retry && !controller.signal.aborted) {
        this.retry = undefined;
        void this.refreshToken();
      }
    }, (error: unknown) => {
      if (!controller.signal.aborted) {
        this.retry = undefined;
        this.logging.logError(error);
      }
    });
  }

  private cancelRetry(): void {
    this.retry?.controller.abort();
    this.retry = undefined;
  }

  private transition(state: AuthState): void {
    const previousState = this.state();

    if (state !== previousState) {
      this.authState.set(state);
      this.logging.trackEvent('auth_state_changed', { previousState, state, refreshFailures: this.failures() });
    }
  }
}
